import React, { useEffect, useState } from "react";
import BlogContext from "../../Context/BlogContext";
import {
  ActionIcon,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import Fuse from "fuse.js";
import {
  IconBookmark,
  IconDots,
  IconMessageCircle,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import { Blog } from "../../Model/Blog.model";
import { formatDate } from "../../Services/common-util.service";
import Seo from "../../seo/Seo";
import { buildCanonicalUrl, createBreadcrumbSchema } from "../../seo/utils";
import { getReadingTimeMinutes } from "../../Services/blog.service";
import { formatReviewCount, getReviewSummary } from "../../Services/review.service";
import { getBlogPath } from "../../utils/routes";
import ReviewsTrustBar from "../Reviews/ReviewsTrustBar";
import "./Blogs.css";

const BLOG_VISUAL_THEMES = [
  {
    background:
      "linear-gradient(135deg, #121212 0%, #2f2f2f 45%, #070707 100%)",
    accent: "#ffffff",
  },
  {
    background:
      "linear-gradient(135deg, #16324f 0%, #2d6a8f 52%, #0f172a 100%)",
    accent: "#eef8ff",
  },
  {
    background:
      "linear-gradient(135deg, #2b2118 0%, #7c3f15 55%, #1f140f 100%)",
    accent: "#fff4e8",
  },
  {
    background:
      "linear-gradient(135deg, #17362b 0%, #2a8f5c 52%, #10261d 100%)",
    accent: "#f3fff9",
  },
];

function Blogs() {
  const { blogs, isLoading } = React.useContext(BlogContext);
  const theme = useMantineTheme();
  const reviewSummary = getReviewSummary();
  const desktopMediaQuery = useMediaQuery(
    `(min-width: ${theme.breakpoints.md})`,
  );
  const [searchText, setSearchText] = useState("");

  function getFilteredBlogs() {
    if (!searchText) {
      return blogs;
    }

    const fuzzySearch = new Fuse(blogs, {
      keys: ["title", "description", "body", "tags", "publishedOn", "cluster"],
      threshold: 0.35,
    });

    return fuzzySearch.search(searchText).map((result) => result.item);
  }

  const filteredBlogs = [...getFilteredBlogs()].sort((leftBlog, rightBlog) => {
    const leftDate = leftBlog.publishedOn
      ? new Date(leftBlog.publishedOn).getTime()
      : 0;
    const rightDate = rightBlog.publishedOn
      ? new Date(rightBlog.publishedOn).getTime()
      : 0;

    return rightDate - leftDate;
  });

  return (
    <Container
      fluid
      className="publication-feed-shell"
      px={desktopMediaQuery ? "xl" : "md"}
    >
      <div className="publication-feed-page">
        <Seo
          title="Student Guides, Study Advice, and Research Tips | SubjectBuddy Blog"
          description="Explore SubjectBuddy blog posts on study planning, research, coding support, project guidance, and academic mentoring for students in the UK."
          path="/blogs"
          keywords={[
            "student blog uk",
            "study advice uk",
            "research tips uk",
            "project guidance blog",
            "coding help blog",
          ]}
          schema={[
            {
              "@context": "https://schema.org",
              "@type": "Blog",
              name: "SubjectBuddy Blog",
              url: buildCanonicalUrl("/blogs"),
              description:
                "Study advice, research guides, and project mentoring articles for students.",
            },
            createBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Blogs", path: "/blogs" },
            ]),
          ]}
        />

        <section className="publication-feed-hero">
          <Text className="publication-feed-kicker">
            SubjectBuddy publication
          </Text>
          <Title order={1} className="publication-feed-title">
            Browse Student guides, study support, and assignment advice.
          </Title>

          <Paper radius="xl" className="publication-feed-search" withBorder>
            <TextInput
              disabled={isLoading}
              placeholder="Search topics, subjects, or keywords"
              size={desktopMediaQuery ? "lg" : "md"}
              radius="xl"
              variant="unstyled"
              classNames={{
                input: "publication-feed-search-input",
                section: "publication-feed-search-section",
              }}
              leftSectionPointerEvents="none"
              leftSectionWidth={42}
              leftSection={<IconSearch size={18} />}
              rightSection={
                searchText ? (
                  <ActionIcon
                    variant="subtle"
                    c="gray"
                    radius="xl"
                    onClick={() => setSearchText("")}
                    aria-label="Clear search"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                ) : null
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Paper>

          {!isLoading ? (
            <Text className="publication-feed-count">
              {filteredBlogs.length} article
              {filteredBlogs.length === 1 ? "" : "s"} ·{" "}
              {reviewSummary.averageRating.toFixed(1)}/5 from{" "}
              {formatReviewCount(reviewSummary.totalReviews)} reviews
            </Text>
          ) : null}
        </section>

        <div className="publication-feed-trust-section">
          <ReviewsTrustBar compact />
        </div>

        <div className="publication-feed-content">
          {isLoading ? (
            <Container h="30vh">
              <Stack
                align="center"
                justify="center"
                style={{ height: "inherit" }}
              >
                <Loader color={theme.colors.green[7]} />
                <Text>Loading blogs ...</Text>
              </Stack>
            </Container>
          ) : (
            <section className="publication-feed-list">
              {filteredBlogs.map((blog, index) => (
                <BlogCard
                  key={`blog-no-${index}`}
                  blog={blog}
                  desktopMediaQuery={desktopMediaQuery}
                />
              ))}
            </section>
          )}
        </div>
      </div>
    </Container>
  );
}

interface BlogCardProps {
  blog: Blog;
  desktopMediaQuery: boolean | undefined;
}

function BlogCard({ blog, desktopMediaQuery }: BlogCardProps) {
  const theme = getBlogVisualTheme(blog);
  const visualTitle = getBlogVisualLabel(blog);
  const [hasImageError, setHasImageError] = useState(false);
  const shouldUseImage = Boolean(blog.image && !hasImageError);
  const shouldShowMedia = Boolean(desktopMediaQuery || shouldUseImage);
  const reviewSummary = getReviewSummary();

  useEffect(() => {
    setHasImageError(false);
  }, [blog.image, blog.slug]);

  return (
    <Paper
      component={Link}
      to={getBlogPath(blog.slug)}
      className="publication-card"
      radius={0}
      withBorder={false}
      p={0}
    >
      <div
        className={`publication-card-grid${
          shouldShowMedia ? "" : " publication-card-grid--text-only"
        }`}
      >
        <Stack
          gap={desktopMediaQuery ? "lg" : "sm"}
          className="publication-card-copy"
        >
          <Group gap={8} wrap="wrap" className="publication-card-publine">
            <Text span className="publication-card-publication">
              In {blog.cluster}
            </Text>
            <Text span className="publication-card-meta">
              {` \u00b7 ${formatDate(blog.publishedOn)} \u00b7 ${getReadingTimeMinutes(blog.body)} min read \u00b7 ${reviewSummary.averageRating.toFixed(1)}/5 student rating`}
            </Text>
          </Group>

          <div className="publication-card-body">
            <Title
              order={desktopMediaQuery ? 2 : 3}
              className="publication-card-title"
            >
              {blog.title}
            </Title>

            <Text className="publication-card-description">
              {blog.description}
            </Text>
          </div>

          <div className="publication-card-footer">
            <div className="publication-card-tags">
              {blog.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="publication-card-tag">
                  {tag}
                </span>
              ))}
            </div>

            <Group gap={14} className="publication-card-actions">
              <Group gap={5} className="publication-card-action">
                <IconMessageCircle size={17} stroke={1.7} />
                <Text span>
                  {Math.max(1, Math.ceil(blog.description.length / 38))}
                </Text>
              </Group>
              <IconBookmark
                size={17}
                stroke={1.7}
                className="publication-card-action-icon"
              />
              <IconDots
                size={17}
                stroke={1.7}
                className="publication-card-action-icon"
              />
            </Group>
          </div>
        </Stack>

        {shouldShowMedia ? (
          shouldUseImage ? (
            <div className="publication-card-media publication-card-media--image">
              <img
                className="publication-card-image"
                src={blog.image}
                alt={getBlogImageAlt(blog)}
                loading="lazy"
                onError={() => setHasImageError(true)}
              />
            </div>
          ) : (
            <div
              className="publication-card-media publication-card-media--visual"
              style={{ background: theme.background, color: theme.accent }}
              aria-hidden="true"
            >
              <div className="publication-card-visual-noise" />
              <div className="publication-card-visual-content">
                <Text className="publication-card-visual-kicker">
                  {blog.cluster}
                </Text>
                <Text className="publication-card-visual-title">
                  {visualTitle}
                </Text>
              </div>
            </div>
          )
        ) : null}
      </div>
    </Paper>
  );
}

function getBlogImageAlt(blog: Blog) {
  return blog.imageAlt || `${blog.title} cover image`;
}

function getBlogVisualTheme(blog: Blog) {
  const seed = `${blog.cluster}-${blog.slug}`
    .split("")
    .reduce((total, char) => total + char.charCodeAt(0), 0);

  return BLOG_VISUAL_THEMES[seed % BLOG_VISUAL_THEMES.length];
}

function getBlogVisualLabel(blog: Blog) {
  const source = blog.tags[0] || blog.targetKeyword || blog.cluster;
  return source
    .split(" ")
    .slice(0, 4)
    .join(" ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default Blogs;
